import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../contexts/CompanyContext';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  Search as SearchIcon,
  XCircle,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SectionTitle from './text_components/SectionTitle';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import useWindowSize from '../hooks/useWindowSize';
import axios from 'axios';
import { Input, Select, Tooltip, message } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import MultiSelect from './InputComponents/MultiSelect';
import { getCurrentUserId } from '../utils/userUtils';
import styled from 'styled-components';
import { format, isSameDay } from 'date-fns';

const { TextArea } = Input;
const { Option } = Select;

// ------ Styled Components (unchanged) ------
const StyledSelect = styled(Select)`
  .ant-select-selector {
    border-radius: 12px !important;
    border: 1px solid rgba(59, 130, 246, 0.2) !important;
    background: rgba(255, 255, 255, 0.7) !important;
    backdrop-filter: blur(8px) !important;
    padding: 4px 16px !important;
    height: 42px !important;
    display: flex !important;
    align-items: center !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 10px 40px -5px rgba(0, 0, 0, 0.1) !important;
  }

  &:hover .ant-select-selector,
  &.ant-select-focused .ant-select-selector {
    border-color: rgba(59, 130, 246, 0.3) !important;
    box-shadow: 0 20px 50px -10px rgba(59, 130, 246, 0.2) !important;
  }

  .ant-select-selection-item {
    font-weight: 500 !important;
    color: #1e40af !important;
    line-height: 34px !important;
  }

  &.ant-select {
    width: 120px !important;
    height: 42px !important;
  }
`;

const WeekViewScroll = styled.div`
  height: calc(100vh - 300px);
  overflow-y: auto;
  
  .week-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 1px;
    min-height: 100%;
  }

  .day-column {
    min-height: 100%;
    min-width: 0;
    
    .day-header {
      position: sticky;
      top: 0;
      background: white;
      z-index: 10;
      padding: 8px;
      border-bottom: 1px solid #e5e7eb;
    }
    
    .events-container {
      padding: 8px;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .event-item {
      white-space: normal;
      word-wrap: break-word;
      min-width: 0;
      line-height: 1.2;
      max-height: none;
      overflow: visible;
      margin-bottom: 4px;
      font-size: 0.75rem;
    }
  }
`;

const YearViewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  height: 100%;
  overflow-y: auto;
  padding: 8px;
  flex: 1; /* Make sure it takes up all available space */

  .month-card {
    background: white;
    border-radius: 12px;
    border: 1px solid rgba(59, 130, 246, 0.2);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 200px;
    height: 100%; /* Make each card fill available height */
  }

  .month-header {
    padding: 8px;
    background: rgba(59, 130, 246, 0.1);
    font-weight: 600;
    text-align: center;
    color: #1e40af;
  }

  .month-content {
    flex: 1;
    padding: 8px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }

  .event-count {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    padding: 4px 8px;
    background: rgba(59, 130, 246, 0.05);
    border-radius: 6px;
  }

  .completion-bar {
    height: 6px;
    background: #e5e7eb;
    border-radius: 3px;
    overflow: hidden;
    
    .complete {
      height: 100%;
      background: #22c55e;
      transition: width 0.3s ease;
    }
  }
`;

const ProgressBar = styled.div`
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
  margin: 4px 0;
  
  .complete {
    height: 100%;
    background: #22c55e;
    transition: width 0.3s ease;
  }
`;

// ------------------------------------------

/**
 * This CompletionModal replicates the logic from @MasterSpreadsheet.js,
 * adapted for the RequirementsCalendar to handle "All Clients" individually.
 */
const CompletionModal = ({ requirement, onClose, year, clients }) => {
  const [loading, setLoading] = useState(false);
  const [localStatuses, setLocalStatuses] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  
  // Add new state variables for API data
  const [checkedData, setCheckedData] = useState({ clients_checked: [] });
  const [notRequiredData, setNotRequiredData] = useState({ not_required: [] });
  const [pendingData, setPendingData] = useState({ pending_clients: [] });

  // Helper to PUT status changes to your server
  const saveStatusesToServer = async (updatedStatuses, previousStatuses) => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('userid');

      // Log before/after states for debugging
      console.log('[CompletionModal] Previous statuses:', previousStatuses);
      console.log('[CompletionModal] Updated statuses:', updatedStatuses);

      // Figure out which changes need to be sent to the API
      const changes = Object.entries(updatedStatuses).reduce((acc, [clientId, newStatus]) => {
        if (previousStatuses[clientId] !== newStatus) {
          // Find the client name using the clientId
          const client = clients.find(c => c.unid === clientId);
          if (client) {
            acc.push({
              clientName: client['Company Name'],
              status: newStatus
            });
          }
        }
        return acc;
      }, []);

      console.log('[CompletionModal] Changes to send:', changes);

      // PUT each status update individually
      const updatePromises = changes.map(({ clientName, status }) => {
        const url = `https://plansync-backend-234288438538.us-central1.run.app/api/requirements-checklist/${year}/${userId}/update-status/${requirement.unid}`;
        const body = {
          company: clientName,
          status: status === 'not required' ? 'not_required' : status.toLowerCase()
        };

        console.log('[CompletionModal] Sending PUT:', { url, body });

        return fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });
      });

      // Wait for all updates to finish
      await Promise.all(updatePromises);

      message.success('Status updated successfully');
      console.log('[CompletionModal] Status update complete successfully');
    } catch (error) {
      console.error('[CompletionModal] Error updating completion statuses:', error);
      message.error('Failed to update completion statuses');
    } finally {
      setLoading(false);
    }
  };

  // Fetch initial statuses on mount
  useEffect(() => {
    const fetchStatuses = async () => {
      setLoading(true);
      try {
        const userId = localStorage.getItem('userid');
        console.log('[CompletionModal] Fetching initial statuses for requirement:', requirement.unid);

        // Initialize every client as 'No' by default
        const initialStatusMap = {};
        clients.forEach((client) => {
          initialStatusMap[client.unid] = 'No';
        });

        // Helper to fetch with error handling
        const fetchWithErrorHandling = async (url) => {
          const response = await fetch(url);
          if (!response.ok) {
            const text = await response.text();
            console.error('[CompletionModal] API Error Response:', {
              status: response.status,
              statusText: response.statusText,
              body: text,
            });
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        };

        // API calls for all three status types
        const [checked, notRequired, pending] = await Promise.all([
          fetchWithErrorHandling(
            `https://plansync-backend-234288438538.us-central1.run.app/api/requirements-checklist/${year}/${userId}/clients-checked/${requirement.unid}`
          ),
          fetchWithErrorHandling(
            `https://plansync-backend-234288438538.us-central1.run.app/api/requirements-checklist/${year}/${userId}/not-required/${requirement.unid}`
          ),
          fetchWithErrorHandling(
            `https://plansync-backend-234288438538.us-central1.run.app/api/requirements-checklist/${year}/${userId}/clients-pending/${requirement.unid}`
          )
        ]);

        // Store API responses in state
        setCheckedData(checked);
        setNotRequiredData(notRequired);
        setPendingData(pending);

        console.log('[CompletionModal] API Response Data:', {
          checked,
          notRequired,
          pending
        });

        // Mark "Yes" clients
        if (checked?.clients_checked) {
          checked.clients_checked.forEach((clientName) => {
            const client = clients.find(c => c['Company Name'] === clientName);
            if (client) {
              initialStatusMap[client.unid] = 'Yes';
            }
          });
        }

        // Mark "Not Required" clients
        if (notRequired?.not_required) {
          notRequired.not_required.forEach((clientName) => {
            const client = clients.find(c => c['Company Name'] === clientName);
            if (client) {
              initialStatusMap[client.unid] = 'Not Required';
            }
          });
        }

        // Mark "No" clients (pending)
        if (pending?.pending_clients) {
          pending.pending_clients.forEach((clientName) => {
            const client = clients.find(c => c['Company Name'] === clientName);
            if (client) {
              initialStatusMap[client.unid] = 'No';
            }
          });
        }

        console.log('[CompletionModal] Final initial status map:', initialStatusMap);
        setLocalStatuses(initialStatusMap);
      } catch (error) {
        console.error('[CompletionModal] Error loading statuses:', error);
        message.error(`Failed to load completion statuses: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (requirement?.unid && year && clients?.length > 0) {
      fetchStatuses();
    } else {
      console.log('[CompletionModal] Missing data, skipping fetch:', {
        requirementId: requirement?.unid,
        year,
        clientCount: clients?.length,
      });
    }
  }, [requirement?.unid, year, clients]);

  // We reuse the same status options from MasterSpreadsheet
  const statusOptions = [
    {
      value: 'Complete',
      label: 'Complete',
      color: '#22c55e',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-700',
    },
    {
      value: 'Not Complete',
      label: 'Not Complete',
      color: '#e11d48',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-700',
    },
    {
      value: 'Not Required',
      label: 'Not Required',
      color: '#475569',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      textColor: 'text-gray-700',
    },
  ];

  // Handle single client toggle
  const handleStatusChange = async (clientId, selectedOption) => {
    console.log('[CompletionModal] Toggling status:', {
      clientId,
      newStatus: selectedOption.value,
    });

    const newStatuses = {
      ...localStatuses,
      [clientId]: selectedOption.value,
    };
    setLocalStatuses(newStatuses);

    // Save only the changed client to server
    await saveStatusesToServer(
      Object.fromEntries(Object.entries(newStatuses).map(([k, v]) => [k, v.toLowerCase()])),
      Object.fromEntries(Object.entries(localStatuses).map(([k, v]) => [k, v.toLowerCase()])),
    );
  };

  // Optional: bulk action if desired
  const handleToggleAll = async (selectedOption) => {
    console.log('[CompletionModal] Bulk action to:', selectedOption.value);
    const newStatuses = {};
    clients.forEach((client) => {
      newStatuses[client.unid] = selectedOption.value;
    });
    setLocalStatuses(newStatuses);

    await saveStatusesToServer(
      Object.fromEntries(Object.entries(newStatuses).map(([k, v]) => [k, v.toLowerCase()])),
      Object.fromEntries(Object.entries(localStatuses).map(([k, v]) => [k, v.toLowerCase()])),
    );
  };

  // Filtered list of clients by name and optional status filter
  const filteredClients = clients.filter((client) => {
    const matchesSearch = client['Company Name']
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesStatus =
      !statusFilter || localStatuses[client.unid] === statusFilter.value;
    return matchesSearch && matchesStatus;
  });

  // Calculate counts based on filtered clients and their statuses
  const getCounts = () => {
    const counts = {
      complete: 0,
      notComplete: 0,
      notRequired: 0
    };

    filteredClients.forEach(client => {
      const status = localStatuses[client.unid];
      if (status === 'Yes') {
        counts.complete++;
      } else if (status === 'No') {
        counts.notComplete++;
      } else if (status === 'Not Required') {
        counts.notRequired++;
      }
    });

    return counts;
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="bg-white/90 backdrop-blur-xl rounded-3xl w-full max-w-[850px] max-h-[90vh] 
          flex flex-col shadow-[0_20px_70px_-10px_rgba(0,0,0,0.2)] overflow-hidden
          border border-white/50"
      >
        {/* Header */}
        <div
          className="px-8 py-6 bg-gradient-to-r from-sky-400/10 via-indigo-500/10 to-blue-800/10
            border-b border-white/30 backdrop-blur-md"
        >
          <div className="flex justify-between items-center">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="h-8 w-1 rounded-full bg-gradient-to-b from-blue-500 to-indigo-600" />
                <h2
                  className="text-xl font-semibold bg-gradient-to-r from-gray-800 to-gray-600 
                    bg-clip-text text-transparent"
                >
                  Update Requirement Status
                </h2>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed max-w-[80%]">
                {requirement?.requirement || requirement?.Requirement}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2.5 hover:bg-white/50 rounded-xl transition-all duration-300
                hover:shadow-lg hover:scale-105 group"
            >
              <XCircle
                size={24}
                className="text-gray-400 group-hover:text-gray-600 transition-colors duration-300"
              />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 space-y-6 relative">
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 z-10 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          )}

          {/* Search and Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 group">
              <input
                type="text"
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 bg-white/50 border border-gray-200 rounded-2xl text-sm
                  transition-all duration-300 placeholder:text-gray-400
                  focus:border-blue-400 focus:ring-4 focus:ring-blue-100 focus:bg-white
                  group-hover:border-gray-300 group-hover:shadow-md"
              />
              <SearchIcon
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400
                  group-hover:text-gray-600 transition-colors duration-300"
              />
            </div>

            <div className="relative w-48">
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: null, label: 'All Statuses' },
                  ...statusOptions,
                ]}
                placeholder="Filter by status..."
                className="w-48"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '1rem',
                    borderColor: '#e5e7eb',
                    minHeight: '52px',
                    backgroundColor: 'rgba(255,255,255,0.5)',
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: '#60a5fa',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    },
                  }),
                  option: (base, { data, isFocused, isSelected }) => ({
                    ...base,
                    backgroundColor: isSelected
                      ? data.color || '#3b82f6'
                      : isFocused
                      ? data.color
                        ? `${data.color}15`
                        : '#f3f4f6'
                      : 'transparent',
                    color: isSelected ? 'white' : data.color || '#374151',
                    fontWeight: '500',
                  }),
                }}
              />
              {statusFilter && (
                <button
                  onClick={() => setStatusFilter(null)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
                >
                  <X size={14} className="text-gray-500" />
                </button>
              )}
            </div>
          </div>

          

          {/* Progress Stats */}
          <div className="mb-6">
            {/* Status Count Cards */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              {(() => {
                const counts = getCounts();
                return [
                  { 
                    label: 'Complete', 
                    count: counts.complete,
                    bgColor: 'bg-green-50',
                    borderColor: 'border-green-200',
                    textColor: 'text-green-700'
                  },
                  { 
                    label: 'Not Complete', 
                    count: counts.notComplete,
                    bgColor: 'bg-red-50',
                    borderColor: 'border-red-200',
                    textColor: 'text-red-700'
                  },
                  { 
                    label: 'Not Required', 
                    count: counts.notRequired,
                    bgColor: 'bg-gray-50',
                    borderColor: 'border-gray-200',
                    textColor: 'text-gray-700'
                  }
                ].map(status => (
                  <div
                    key={status.label}
                    className={`${status.bgColor} ${status.borderColor} border rounded-2xl p-4 shadow-sm`}
                  >
                    <div className={`text-2xl font-bold ${status.textColor}`}>
                      {status.count}
                    </div>
                    <div className="text-sm text-gray-600">
                      {status.label}
                    </div>
                  </div>
                ));
              })()}
            </div>
            
            {/* Overall Progress Bar - Removed */}
          </div>

          {/* Client List */}
          {filteredClients.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No clients match your current filters
            </div>
          ) : (
            <div className="space-y-3">
              {filteredClients.map((client) => (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  key={client.unid}
                  className={`flex items-center justify-between p-5
                    rounded-2xl transition-all duration-300 group
                    hover:shadow-lg border hover:scale-[1.01]
                    ${
                      localStatuses[client.unid] === 'Yes'
                        ? 'bg-green-50/50 hover:bg-green-50 border-green-200/50'
                        : localStatuses[client.unid] === 'No'
                        ? 'bg-red-50/50 hover:bg-red-50 border-red-200/50'
                        : 'bg-gray-50/50 hover:bg-gray-50 border-gray-200/50'
                    }`}
                >
                  <div className="flex items-center gap-4">
                    {/* Colored status dot */}
                    <div
                      className={`w-3 h-3 rounded-full ring-2 ring-white shadow-sm transition-all duration-300 ${
                        localStatuses[client.unid] === 'Yes'
                          ? 'bg-green-500 ring-green-200'
                          : localStatuses[client.unid] === 'No'
                          ? 'bg-red-500 ring-red-200'
                          : 'bg-gray-500 ring-gray-200'
                      }`}
                    />
                    <span className="text-[0.95rem] font-medium text-gray-700">
                      {client['Company Name']}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      // Cycle through statuses in a quick toggle
                      const currentStatus = localStatuses[client.unid];
                      let nextStatus;
                      switch (currentStatus) {
                        case 'No':
                          nextStatus = 'Yes';
                          break;
                        case 'Yes':
                          nextStatus = 'Not Required';
                          break;
                        case 'Not Required':
                        default:
                          nextStatus = 'No';
                          break;
                      }
                      handleStatusChange(client.unid, {
                        value: nextStatus,
                        label: nextStatus,
                      });
                    }}
                    className={`px-4 py-2 rounded-xl transition-all duration-300 font-medium text-sm
                      ${
                        localStatuses[client.unid] === 'Yes'
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : localStatuses[client.unid] === 'No'
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }
                      hover:shadow-md hover:scale-[1.02]`}
                  >
                    {localStatuses[client.unid] === 'Yes'
                      ? 'Complete'
                      : localStatuses[client.unid] === 'No'
                      ? 'Not Complete'
                      : 'Not Required'}
                  </button>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-8 py-6 bg-gradient-to-r from-gray-50 to-white border-t border-gray-100
            backdrop-blur-md flex justify-end"
        >
          <button
            onClick={onClose}
            className="px-6 py-3 text-gray-600 font-medium hover:bg-gray-100 
              rounded-xl transition-all duration-300 hover:shadow-md hover:scale-[1.02]"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
};


// ------------------------------------------
const RequirementsCalendar = () => {
  const navigate = useNavigate();
  const { isCustomerView } = useCompany();

  // Redirect customer users away from requirements calendar
  useEffect(() => {
    if (isCustomerView) {
      navigate('/spreadsheet/client-list');
    }
  }, [isCustomerView, navigate]);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState({
    company_names: [],
    quarters: [],
    requirement: '',
    stakeholders: [],
    complete: 'No',
    due_dates: {},
    priority: 'No',
    add_to_annual_plan_review: 'No',
    include_in_next_years_master_spreadsheet: 'No',
    rewrite_with_ai: false,
    isAllDay: true,
    date: new Date(),
    time: '',
  });
  const [view, setView] = useState('month');
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [currentWeek, setCurrentWeek] = useState([]);
  const [showMonthYearSelector, setShowMonthYearSelector] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth());
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [requirements, setRequirements] = useState([]);
  const { width } = useWindowSize();
  const isMobile = width <= 768;
  const [companyOptions, setCompanyOptions] = useState([]);
  const [globalSearch, setGlobalSearch] = useState('');
  
  // Define dummy placeholder functions for what was removed
  const handleEventClick = (event) => {
    console.log('[RequirementsCalendar] Event clicked:', event);
    
    // Only handle navigation between views
    if (view === 'month') {
      setView('day');
      setCurrentDate(event.date);
    }
    // No status updates for any view
  };

  // Add these helper functions at the top of your component
  const getDaysInMonth = () => {
    const year = currentDate.getFullYear().toString();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Get the first Sunday before or on the first day of the month
    const start = new Date(firstDay);
    start.setDate(firstDay.getDate() - firstDay.getDay());
    
    // Get the last Saturday after or on the last day of the month
    const end = new Date(lastDay);
    end.setDate(lastDay.getDate() + (6 - lastDay.getDay()));
    
    const days = [];
    const current = new Date(start);
    
    while (current <= end) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  // ----- Fetch client names for the multi-select -----
  useEffect(() => {
    const fetchClientNames = async () => {
      try {
        const userId = localStorage.getItem('userid');
        console.log("USER ID: ", userId)
        const year = new Date().getFullYear().toString();
        console.log("DEBUGGING YEAR: ", year);
        const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/client_names/${year}`, {
          headers: {
            'user-id': userId,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const options = data.client_names.map((name) => ({
          value: name,
          label: name,
        }));
        setCompanyOptions(options);
      } catch (error) {
        console.error('Error fetching client names:', error);
      }
    };

    fetchClientNames();
  }, []);

  // ----- Fetch requirements from server -----
  const fetchRequirements = useCallback(async () => {
    try {
      const userId = getCurrentUserId();
      const year = currentDate.getFullYear().toString();
      console.log("DEBUGGING YEAR: ", year);
      const response = await axios.get(
        `https://plansync-backend-234288438538.us-central1.run.app/api/requirements_checklist/${userId}/${year}`
      );

      const formattedRequirements = response.data.requirements
        .map((req) => {
          const date = parseDate(req.dueDate, year, req.quarter);

          if (!date) {
            console.warn(`Skipping requirement with invalid date format: ${req.dueDate}`);
            return null;
          }

          // Ensure company_names is always an array
          const company_names = Array.isArray(req.company)
            ? req.company
            : typeof req.company === 'string'
            ? req.company.split(',').map((c) => c.trim())
            : [];

          // Debug
          console.log('[RequirementsCalendar] Processing requirement:', {
            original: req,
            company_names,
            date,
            isAllClients: company_names.includes('All Clients'),
          });

          return {
            ...req,
            date,
            company_names,
            title: `${company_names.join(', ')}: ${req.requirement}`,
            isRequirement: true,
            isAllDay: true,
          };
        })
        .filter(Boolean);

      setRequirements(formattedRequirements);
    } catch (error) {
      console.error('Error fetching requirements:', error);
    }
  }, [currentDate]);

  // ----- Build the weekly dates -----
  const updateWeek = useCallback(() => {
    const weekStart = new Date(currentDate);
    // Sunday as start of the week:
    weekStart.setDate(currentDate.getDate() - currentDate.getDay());
    const week = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      week.push(day);
    }
    setCurrentWeek(week);
  }, [currentDate]);

  // ----- UseEffects -----
  useEffect(() => {
    // We only fetch if the month or year changed (for example):
    // (If you always want to fetch on ANY currentDate day change, just leave it as [currentDate])
    fetchRequirements();
    updateWeek();
  }, [currentDate]); // <-- Keep the array minimal, e.g. just [currentDate]

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (view === 'day' || view === 'week') {
          setView('month');
        }
        if (showMonthYearSelector) {
          setShowMonthYearSelector(false);
        }
      }
    };
    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [view, showMonthYearSelector]);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        // if we have a dialog open
        const activeElement = document.activeElement;
        const isInputFocused =
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'SELECT' ||
          activeElement.tagName === 'TEXTAREA';

        if (!isInputFocused && showEventDialog) {
          setShowEventDialog(false);
        }
      }
    };

    if (showEventDialog) {
      window.addEventListener('keydown', handleEscapeKey);
    }
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [showEventDialog]);

  // ----- Access trap for event dialog -----
  useEffect(() => {
    if (!showEventDialog) return;
    const modal = document.querySelector('.requirements-modal');
    if (!modal) return;

    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement =
      focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstFocusableElement) {
            e.preventDefault();
            lastFocusableElement.focus();
          }
        } else {
          if (document.activeElement === lastFocusableElement) {
            e.preventDefault();
            firstFocusableElement.focus();
          }
        }
      }
    };

    modal.addEventListener('keydown', handleTabKey);
    firstFocusableElement.focus();

    return () => {
      modal.removeEventListener('keydown', handleTabKey);
    };
  }, [showEventDialog]);

  // ----- Navigation (prev/next) -----
  const prevPeriod = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') newDate.setMonth(newDate.getMonth() - 1);
    else if (view === 'week') newDate.setDate(newDate.getDate() - 7);
    else newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };
  const nextPeriod = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') newDate.setMonth(newDate.getMonth() + 1);
    else if (view === 'week') newDate.setDate(newDate.getDate() + 7);
    else newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  // ----- Add Requirement -----
  const addEvent = async () => {
    try {
      const userId = localStorage.getItem('userid');
      const year = new Date().getFullYear().toString();
      console.log("DEBUGGING YEAR: ", year);
      const selectedDate = new Date(newEvent.date);
      const formattedDate = `${selectedDate.getFullYear()}-${String(
        selectedDate.getMonth() + 1
      ).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;

      const formData = {
        company_names: newEvent.company_names,
        quarters: newEvent.quarters,
        requirement: newEvent.requirement,
        stakeholders: newEvent.stakeholders,
        complete: newEvent.complete,
        due_dates: newEvent.quarters.reduce(
          (acc, quarter) => ({
            ...acc,
            [quarter]: formattedDate,
          }),
          {}
        ),
        priority: newEvent.priority,
        add_to_annual_plan_review: newEvent.add_to_annual_plan_review,
        include_in_next_years_master_spreadsheet:
          newEvent.include_in_next_years_master_spreadsheet,
        rewrite_with_ai: newEvent.rewrite_with_ai,
      };

      const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/requirements/${year}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': userId,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      message.success(data.message || 'Requirement added successfully');

      // Add to local list
      const newRequirement = {
        ...formData,
        date: newEvent.date,
        isAllDay: true,
        title: `${formData.company_names.join(', ')}: ${formData.requirement}`,
      };
      setEvents((prevEvents) => [...prevEvents, newRequirement]);
      setNewEvent({
        company_names: [],
        quarters: [],
        requirement: '',
        stakeholders: [],
        complete: 'No',
        due_dates: {},
        priority: 'No',
        add_to_annual_plan_review: 'No',
        include_in_next_years_master_spreadsheet: 'No',
        rewrite_with_ai: false,
        isAllDay: true,
        date: new Date(),
        time: '',
      });
      setShowEventDialog(false);
      await fetchRequirements();
    } catch (error) {
      console.error('Error adding requirement:', error);
      message.error('Failed to add requirement');
    }
  };

  // ----- Helpers -----
  const parseDate = (dueDateStr, year, quarter = null) => {
    if (!dueDateStr) return null;
    const dateStr = dueDateStr.trim();
    let date = null;

    if (dateStr.toLowerCase() === 'quarter end') {
      if (!quarter) return null;
      const quarterEndMonths = [2, 5, 8, 11];
      date = new Date(year, quarterEndMonths[parseInt(quarter, 10) - 1] + 1, 0);
    } else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
      const [month, day, yearStr] = dateStr.split('/');
      date = new Date(
        parseInt(yearStr, 10),
        parseInt(month, 10) - 1,
        parseInt(day, 10)
      );
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      date = new Date(dateStr);
    }
    return date && !isNaN(date.getTime()) ? date : null;
  };

  const filterRequirements = (requirements, searchTerm) => {
    if (!searchTerm) return requirements;
    const searchLower = searchTerm.toLowerCase();
    return requirements.filter((req) => {
      const companyMatch = Array.isArray(req.company_names)
        ? req.company_names.some((c) => c.toLowerCase().includes(searchLower))
        : req.company?.toLowerCase().includes(searchLower);
      const requirementMatch = req.requirement?.toLowerCase().includes(searchLower);
      const stakeholderMatch = req.stakeholders?.toLowerCase().includes(searchLower);
      const quarterMatch = Array.isArray(req.quarters)
        ? req.quarters.some((q) => q.toLowerCase().includes(searchLower))
        : req.quarter?.toString().toLowerCase().includes(searchLower);
      const statusMatch = req.complete?.toLowerCase().includes(searchLower);

      return (
        companyMatch ||
        requirementMatch ||
        stakeholderMatch ||
        quarterMatch ||
        statusMatch
      );
    });
  };

  const getEventsForDate = (date) => {
    // Utility to format date consistently (YYYY-MM-DD)
    const formatDate = (dateInput) => {
      if (!dateInput) return null;
      if (
        typeof dateInput === 'string' &&
        dateInput.match(/^\d{4}-\d{2}-\d{2}$/)
      ) {
        return dateInput;
      }
      const d = new Date(dateInput);
      if (isNaN(d.getTime())) return null;
      d.setMinutes(d.getMinutes() + d.getTimezoneOffset());
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        '0'
      )}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const targetDate = formatDate(date);
    if (!targetDate) return [];

    const dateEvents = events.filter((event) => {
      const eventDate = formatDate(event.date);
      return eventDate === targetDate;
    });

    // Also apply global search to requirements
    const filtered = filterRequirements(requirements, globalSearch);
    const requirementsForDate = filtered.filter((req) => {
      if (req.dueDate) {
        return formatDate(req.dueDate) === targetDate;
      }
      if (req.due_dates && Object.values(req.due_dates)[0]) {
        const dueDate = formatDate(Object.values(req.due_dates)[0]);
        return dueDate === targetDate;
      }
      return false;
    });

    return [...dateEvents, ...requirementsForDate].sort((a, b) => {
      if (a.isAllDay && !b.isAllDay) return -1;
      if (!a.isAllDay && b.isAllDay) return 1;
      return a.time ? a.time.localeCompare(b.time) : -1;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // ----- Month View -----
  const getCalendarRowCount = () => {
    const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    // Calculate total days needed (including padding days)
    const totalDays = startDayOfWeek + daysInMonth;
    
    // Calculate number of rows needed (ceil to account for partial weeks)
    return Math.ceil(totalDays / 7);
  };

  // Update the renderMonthView function
  const renderMonthView = () => {
    const rowCount = getCalendarRowCount();
    
    return (
      <div className="h-full flex flex-col p-4">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Requirements Calendar</h1>
        
        {/* Calendar Grid - Modified to fill entire available space */}
        <div className={`flex-1 grid grid-cols-7 grid-rows-[auto_repeat(${rowCount},1fr)] h-full gap-1 overflow-hidden`}>
          {/* Day Headers */}
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div
              key={day}
              className="text-center py-1 font-semibold text-gray-600"
            >
              {day}
            </div>
          ))}

          {/* Calendar Days */}
          {getDaysInMonth().slice(0, rowCount * 7).map((date, index) => {
            const events = getEventsForDate(date);
            const isCurrentMonth = date.getMonth() === currentDate.getMonth();
            const isToday = isSameDay(date, new Date());
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className={`
                  relative p-1.5 bg-white rounded-lg border
                  transition-all duration-200
                  h-full w-full flex flex-col
                  ${isCurrentMonth 
                    ? 'border-blue-100 hover:border-blue-300' 
                    : 'border-gray-100 bg-gray-50'
                  }
                  ${isToday ? 'ring-2 ring-blue-500 ring-opacity-50' : ''}
                  cursor-pointer hover:shadow-sm
                `}
                onClick={() => {
                  setCurrentDate(date);
                  setView('day');
                }}
              >
                {/* Date Number */}
                <div className={`
                  text-right text-sm font-medium mb-0.5
                  ${isCurrentMonth ? 'text-gray-700' : 'text-gray-400'}
                  ${isToday ? 'text-blue-600' : ''}
                `}>
                  {date.getDate()}
                </div>

                {/* Events List - Modified to better use space */}
                <div className="flex-1 overflow-y-auto">
                  {events.length > 0 && (
                    <div className="space-y-0.5 flex flex-col">
                      {events.map((event, eventIndex) => {
                        // Only show first 3 events directly
                        if (eventIndex < 3) {
                          const isAllClientsRequirement =
                            event.company_names?.includes('All Clients') ||
                            event.company?.includes('All Clients');
                            
                          return (
                            <div
                              key={eventIndex}
                              className={`
                                text-xs p-0.5 px-1.5 rounded-md truncate
                                ${isAllClientsRequirement
                                  ? 'bg-blue-50 text-gray-700'
                                  : event.complete === 'Yes'
                                    ? 'bg-green-50 text-green-700'
                                    : 'bg-red-50 text-red-700'
                                }
                              `}
                            >
                              {event.title.length > 25 
                                ? event.title.substring(0, 25) + '...' 
                                : event.title}
                            </div>
                          );
                        }
                        return null;
                      })}
                      {events.length > 3 && (
                        <div className="text-xs text-blue-500 pl-1 mt-0.5">
                          +{events.length - 3} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    );
  };

  // ----- Week View -----
  const renderWeekView = () => {
    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    return (
      <WeekViewScroll>
        {/* Weekly Progress Stats removed */}

        <div className="week-grid">
          {currentWeek.map((date, index) => (
            <motion.div key={index} variants={itemVariants} className="day-column">
              <div
                className={`day-header ${
                  isToday(date)
                    ? 'bg-gradient-to-br from-blue-100 to-blue-200'
                    : 'bg-white'
                }`}
              >
                <div
                  className={`font-bold ${
                    isToday(date) ? 'text-blue-600' : 'text-gray-700'
                  } ${isMobile ? 'text-center' : ''}`}
                >
                  {isMobile ? (
                    <>
                      <div>{daysOfWeek[date.getDay()].charAt(0)}</div>
                      <div>{date.getDate()}</div>
                    </>
                  ) : (
                    `${daysOfWeek[date.getDay()]} ${date.getDate()}`
                  )}
                </div>
              </div>
              <div className="events-container">
                {getEventsForDate(date).map((event, eventIndex) => (
                  <div
                    key={eventIndex}
                    className={`event-item rounded px-2 py-1 cursor-pointer hover:opacity-90 ${
                      event.isRequirement
                        ? event.complete === 'Yes'
                          ? 'bg-green-50 border border-green-200 text-green-700'
                          : 'bg-red-50 border border-red-200 text-red-700'
                        : 'bg-blue-500 text-white'
                    }`}
                    onClick={() => handleEventClick(event)}
                  >
                    {event.title}
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </WeekViewScroll>
    );
  };

  // ----- Year View -----
  const renderYearView = () => {
    const months = [
      'January', 'February', 'March', 'April',
      'May', 'June', 'July', 'August',
      'September', 'October', 'November', 'December'
    ];

    return (
      <YearViewGrid>
        {months.map((month, index) => {
          const firstDay = new Date(currentDate.getFullYear(), index, 1);
          const lastDay = new Date(currentDate.getFullYear(), index + 1, 0);

          // Collect all events for the month
          const monthEvents = [];
          let d = new Date(firstDay);
          while (d <= lastDay) {
            monthEvents.push(...getEventsForDate(d));
            d.setDate(d.getDate() + 1);
          }

          // Get unique events (remove duplicates by title)
          const uniqueEvents = Array.from(
            new Map(monthEvents.map(item => [item.title, item])).values()
          );

          return (
            <motion.div
              key={month}
              variants={itemVariants}
              className="month-card"
              onClick={() => {
                setCurrentDate(new Date(currentDate.getFullYear(), index, 1));
                setView('month');
              }}
            >
              <div className="month-header">
                {month} {currentDate.getFullYear()}
              </div>
              <div className="month-content">
                {/* Event previews */}
                <div className="space-y-1 flex-1 overflow-y-auto">
                  {uniqueEvents.map((event, i) => {
                    // Directly show all events without artificial limit
                    const isAllClientsRequirement =
                      event.company_names?.includes('All Clients') ||
                      event.company?.includes('All Clients');
                    
                    return (
                      <div
                        key={i}
                        className={`text-xs p-1.5 rounded-lg truncate ${
                          isAllClientsRequirement
                            ? 'bg-blue-50 text-blue-700 border border-blue-200'
                            : event.complete === 'Yes'
                              ? 'bg-green-50 text-green-700 border border-green-200'
                              : 'bg-red-50 text-red-700 border border-red-200'
                        }`}
                      >
                        {event.title}
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          );
        })}
      </YearViewGrid>
    );
  };

  // ----- Month/Year modal -----
  const handleMonthYearChange = () => {
    const newDate = new Date(selectedYear, selectedMonth, 1);
    setCurrentDate(newDate);
    setShowMonthYearSelector(false);
  };
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleMonthYearChange();
    }
  };
  const closeMonthYearSelector = () => {
    setShowMonthYearSelector(false);
  };

  const renderLabel = (label, tooltip) => (
    <div className="flex items-center">
      <span>{label}</span>
      <Tooltip title={tooltip}>
        <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
      </Tooltip>
    </div>
  );

  // Update handleViewChange to just change the view without fetching statuses
  const handleViewChange = (newView) => {
    setView(newView);
    // No need for fetchStatusesForEvent and setEventStatuses since we're not updating statuses
  };

  // Add handleDateClick function
  const handleDateClick = (date) => {
    setView('day');
    setCurrentDate(date);
  };

  // ----- Renders -----
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.005,
      },
    },
  };
  const itemVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
      },
    },
  };

  // Add this new function after the other helper functions but before the render functions
  const handleStatusUpdate = () => {
    // Status updates are disabled
    console.log('[RequirementsCalendar] Status updates are disabled');
  };

  // ----- Day View -----
  const renderDayView = () => {
    const dateEvents = getEventsForDate(currentDate);

    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 gap-4 h-full overflow-y-auto p-4"
      >
        <div className="space-y-4">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="text-xl font-bold text-gray-800 mb-4">
              {currentDate.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </div>
          </div>

          {dateEvents.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No tasks or events scheduled for this day
            </div>
          ) : (
            dateEvents.map((event, index) => {
              const isAllClientsRequirement =
                event.company_names?.includes('All Clients') ||
                event.company?.includes('All Clients');

              // Set background color based on requirement type and status
              let bgColorClass;
              if (isAllClientsRequirement) {
                bgColorClass = 'bg-blue-50 border-blue-100';
              } else if (event.complete === 'Yes') {
                bgColorClass = 'bg-green-50 border-green-200';
              } else {
                bgColorClass = 'bg-red-50 border-red-200';
              }

              return (
                <motion.div
                  key={`${event.title}-${index}`}
                  variants={itemVariants}
                  className={`rounded-lg p-4 shadow-md transition-all duration-200 ${bgColorClass} border`}
                  onClick={() => handleEventClick(event)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-gray-800">{event.title}</h3>
                    {!isAllClientsRequirement && (
                      <span
                        className={`text-sm px-3 py-1 rounded-full ${
                          event.complete === 'Yes'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {event.complete === 'Yes' ? 'Complete' : 'Not Complete'}
                      </span>
                    )}
                  </div>
                  {event.isRequirement && (
                    <div className="space-y-2 mt-3 text-sm text-gray-600">
                      {!isAllClientsRequirement && event.complete && (
                        <div className="flex justify-between">
                          <span>Status:</span>
                          <span className={event.complete === 'Yes' ? 'text-green-600' : 'text-red-600'}>
                            {event.complete === 'Yes' ? 'Complete' : 'Not Complete'}
                          </span>
                        </div>
                      )}
                      {event.stakeholder && (
                        <div className="flex justify-between">
                          <span>Responsible:</span>
                          <span>{event.stakeholder}</span>
                        </div>
                      )}
                      {event.quarter && (
                        <div className="flex justify-between">
                          <span>Quarter:</span>
                          <span>Q{event.quarter.toString().replace('Q', '')}</span>
                        </div>
                      )}
                      {event.dueDate && (
                        <div className="flex justify-between">
                          <span>Due Date:</span>
                          <span>{event.dueDate}</span>
                        </div>
                      )}
                      {event.stakeholders && (
                        <div className="flex justify-between">
                          <span>Stakeholders:</span>
                          <span>{event.stakeholders}</span>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              );
            })
          )}
        </div>
      </motion.div>
    );
  };

  // Update the renderEventItem function to not show status for "All Clients" requirements
  const renderEventItem = (event, isCompact = false) => {
    const isAllClientsRequirement =
      event.company_names?.includes('All Clients') ||
      event.company?.includes('All Clients');

    const getStatusColor = (status, isAllClientsRequirement) => {
      // For "All Clients" requirements, use neutral colors
      if (isAllClientsRequirement) {
        return 'bg-blue-50 border-blue-100';
      }
      
      // For regular requirements, use status-based colors (read-only)
      switch (status?.toLowerCase()) {
        case 'yes':
        case 'complete':
        case 'completed':
          return 'bg-green-50 border-green-200';
        case 'no':
        case 'not complete':
        case 'incomplete':
          return 'bg-red-50 border-red-200';
        case 'not required':
          return 'bg-gray-50 border-gray-200';
        default:
          return 'bg-blue-50 border-blue-200';
      }
    };

    // Month view => compact
    if (isCompact) {
      return (
        <motion.div
          key={`${event.title}-${event.date}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className={`text-xs ${getStatusColor(event.complete, isAllClientsRequirement)} border rounded-lg px-2 py-1 mt-1 truncate shadow-md cursor-pointer`}
          onClick={() => handleEventClick(event)}
        >
          <div className="flex justify-between items-center">
            <span className="truncate">{event.title}</span>
            {!isAllClientsRequirement && (
              <span className="text-xs text-gray-600 ml-1 whitespace-nowrap">
                {event.complete === 'Yes' ? '✓' : '○'}
              </span>
            )}
          </div>
        </motion.div>
      );
    }

    // Day/Week view => detailed (read-only)
    return (
      <motion.div
        key={`${event.title}-${event.date}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={`${getStatusColor(event.complete, isAllClientsRequirement)} border rounded-lg p-4 mb-4 shadow-md space-y-2 cursor-pointer hover:shadow-lg transition-shadow`}
        onClick={() => handleEventClick(event)}
      >
        <div className="flex justify-between items-start">
          <h3 className="font-semibold text-gray-800">{event.title}</h3>
          {!isAllClientsRequirement && (
            <span className={`text-sm px-3 py-1 rounded-full
              ${event.complete?.toLowerCase() === 'yes'
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'}`}
            >
              {event.complete?.toLowerCase() === 'yes' ? 'Complete' : 'Not Complete'}
            </span>
          )}
        </div>
        <div className="grid gap-2 text-sm">
          {!isAllClientsRequirement && (
            <div className="flex justify-between">
              <span className="text-gray-600">Status:</span>
              <span
                className={`font-medium ${
                  event.complete === 'Yes' ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {event.complete === 'Yes' ? 'Complete' : 'Not Complete'}
              </span>
            </div>
          )}
          {event.quarters && event.quarters.length > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-600">Quarter:</span>
              <span className="text-gray-800">{event.quarters.join(', ')}</span>
            </div>
          )}
          {event.stakeholders && event.stakeholders.length > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-600">Stakeholders:</span>
              <span className="text-gray-800">
                {event.stakeholders.join(', ')}
              </span>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className={`flex flex-col h-screen max-w-[100vw] mx-auto p-4 font-roboto overflow-hidden ${isMobile ? 'text-sm' : ''}`}
    >
      <motion.div variants={itemVariants}>
        {/* <SectionTitle style={{ marginBottom: '1rem' }}>
          Requirements Calendar
        </SectionTitle> */}
      </motion.div>

      <motion.div
        variants={itemVariants}
        className="bg-white rounded-3xl border border-blue-200 shadow-2xl p-2 sm:p-4 flex flex-col flex-grow overflow-hidden mb-4"
      >
        {/* Top Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 space-y-2 sm:space-y-0">
          <div className="flex items-center">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="p-1 sm:p-2 rounded-full bg-gradient-to-bl from-sky-400 to-blue-800 text-white shadow-md transition-all duration-200 mr-4"
              onClick={prevPeriod}
            >
              <ChevronLeft size={isMobile ? 16 : 20} />
            </motion.button>
            
            {/* Fixed width container for month/year */}
            <div className="w-[200px] text-center"> {/* Adjust width as needed */}
              <button
                onClick={() => setShowMonthYearSelector(true)}
                className={`text-lg sm:text-2xl font-bold text-gray-800
                  hover:text-blue-600 transition-colors`}
              >
                {view === 'month' &&
                  currentDate.toLocaleString('default', {
                    month: 'long',
                    year: 'numeric',
                  })}
                {view === 'week' && `Week of ${currentWeek[0].toLocaleDateString()}`}
                {view === 'day' && currentDate.toLocaleDateString()}
                {view === 'year' && currentDate.getFullYear()}
              </button>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="p-1 sm:p-2 rounded-full bg-gradient-to-bl from-sky-400 to-blue-800 text-white shadow-md transition-all duration-200 ml-4"
              onClick={nextPeriod}
            >
              <ChevronRight size={isMobile ? 16 : 20} />
            </motion.button>
          </div>
          
          {/* Rest of the top bar content */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            <StyledSelect
              value={view}
              onChange={handleViewChange}
              options={[
                { value: 'year', label: 'Year' },
                { value: 'month', label: 'Month' },
                { value: 'week', label: 'Week' },
                { value: 'day', label: 'Day' },
              ]}
            />
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center bg-gradient-to-bl from-sky-400 to-blue-800 text-white px-2 py-1 sm:px-3 sm:py-2 hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md text-sm sm:text-base"
              onClick={() => setShowEventDialog(true)}
              style={{ borderRadius: '12px' }}
            >
              <Plus size={isMobile ? 14 : 18} className="mr-1 sm:mr-2" />{' '}
              {isMobile ? 'Add' : 'Add New Requirement'}
            </motion.button>
          </div>
        </div>

        {/* Global Search */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              placeholder="Search requirements by company, description, stakeholders..."
              className="w-full px-4 py-2 pl-10 pr-4 text-gray-700 bg-white border border-blue-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
            <SearchIcon
              size={18}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            {globalSearch && (
              <button
                onClick={() => setGlobalSearch('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
        {/* Main Calendar Container */}
        <div className="flex-grow overflow-hidden rounded-2xl border border-blue-200 shadow-inner p-2 sm:p-4 bg-gradient-to-br from-white to-blue-50">
          <AnimatePresence mode="wait">
            {view === 'year' && renderYearView()}
            {view === 'month' && renderMonthView()}
            {view === 'week' && renderWeekView()}
            {view === 'day' && renderDayView()}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Month/Year Selector Modal */}
      <AnimatePresence>
        {showMonthYearSelector && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-filter backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-white p-8 rounded-3xl w-full max-w-md shadow-2xl relative"
            >
              <button
                onClick={closeMonthYearSelector}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X size={24} />
              </button>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">
                Select Month and Year
              </h3>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="monthSelect"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Month
                  </label>
                  <select
                    id="monthSelect"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    onKeyDown={handleKeyDown}
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {[
                      'January','February','March','April','May','June',
                      'July','August','September','October','November','December',
                    ].map((m, idx) => (
                      <option key={m} value={idx}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="yearInput"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Year
                  </label>
                  <input
                    id="yearInput"
                    type="number"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    onKeyDown={handleKeyDown}
                    min="1900"
                    max="2100"
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={closeMonthYearSelector}
                  className="px-4 py-2 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMonthYearChange}
                  className="px-4 py-2 bg-gradient-to-bl from-sky-400 to-blue-800 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-colors shadow-md"
                >
                  Go
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add New Requirement Modal */}
      {showEventDialog && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="requirements-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-filter backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="requirements-modal bg-white p-8 rounded-3xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto relative"
          >
            <div className="flex flex-col mb-6">
              <h3 className="text-2xl font-bold text-gray-800">
                Add New Requirement
              </h3>
              <p className="text-sm text-gray-600 mt-2">
                Any requirements that you add will be visible in both the
                Requirements Checklist and Requirements Calendar.
              </p>
              <button
                onClick={() => setShowEventDialog(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              {/* Company Names */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {renderLabel(
                    'Company Name',
                    'Select one or more companies to apply these requirements to.'
                  )}
                </label>
                <MultiSelect
                  options={companyOptions}
                  selectedValues={newEvent.company_names}
                  onChange={(value) => setNewEvent({ ...newEvent, company_names: value })}
                  placeholder="Select company(s)"
                />
              </div>
              {/* Quarters */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {renderLabel('Quarter', 'Select which quarter(s) these requirements apply to.')}
                </label>
                <MultiSelect
                  options={[
                    { value: 'Q1', label: 'Q1' },
                    { value: 'Q2', label: 'Q2' },
                    { value: 'Q3', label: 'Q3' },
                    { value: 'Q4', label: 'Q4' },
                  ]}
                  selectedValues={newEvent.quarters}
                  onChange={(value) => setNewEvent({ ...newEvent, quarters: value })}
                  placeholder="Select quarter(s)"
                />
              </div>
              {/* Requirement */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {renderLabel('Requirement', 'Describe the specific requirement or task.')}
                </label>
                <TextArea
                  value={newEvent.requirement}
                  onChange={(e) => setNewEvent({ ...newEvent, requirement: e.target.value })}
                  rows={3}
                  placeholder="Enter requirement details"
                />
              </div>
              {/* Stakeholders */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {renderLabel('Stakeholders', 'Select all parties involved.')}
                </label>
                <MultiSelect
                  options={[
                    { value: 'Plan Sponsor', label: 'Plan Sponsor' },
                    { value: 'Participants', label: 'Participants' },
                    { value: 'TPA', label: 'TPA' },
                    { value: 'Recordkeeper', label: 'Recordkeeper' },
                  ]}
                  selectedValues={newEvent.stakeholders}
                  onChange={(value) => setNewEvent({ ...newEvent, stakeholders: value })}
                  placeholder="Select stakeholder(s)"
                />
              </div>
              {/* Due Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {renderLabel('Date', 'Select the date when this requirement is due')}
                </label>
                <DatePicker
                  selected={newEvent.date}
                  onChange={(date) => setNewEvent({ ...newEvent, date })}
                  dateFormat="MMMM d, yyyy"
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {/* All Day Toggle */}
              <div className="flex items-center">
                <input
                  id="allDayEvent"
                  type="checkbox"
                  checked={newEvent.isAllDay}
                  onChange={(e) =>
                    setNewEvent({
                      ...newEvent,
                      isAllDay: e.target.checked,
                      time: e.target.checked ? '' : newEvent.time,
                    })
                  }
                  className="mr-2"
                />
                <label htmlFor="allDayEvent" className="text-sm font-medium text-gray-700">
                  All Day Event
                </label>
              </div>
              {!newEvent.isAllDay && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                  <input
                    type="time"
                    value={newEvent.time}
                    onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
              {/* Complete / Priority */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {renderLabel('Complete', 'Mark the current status.')}
                  </label>
                  <Select
                    value={newEvent.complete}
                    onChange={(value) => setNewEvent({ ...newEvent, complete: value })}
                    style={{ width: '100%' }}
                  >
                    <Option value="Yes">Yes</Option>
                    <Option value="No">No</Option>
                    <Option value="Not Required">Not Required</Option>
                    <Option value="Submitted">Submitted</Option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {renderLabel('Priority', 'Is this a high priority requirement?')}
                  </label>
                  <Select
                    value={newEvent.priority}
                    onChange={(value) => setNewEvent({ ...newEvent, priority: value })}
                    style={{ width: '100%' }}
                  >
                    <Option value="High">High</Option>
                    <Option value="No">No</Option>
                  </Select>
                </div>
              </div>
              {/* Additional fields */}
              <div className="space-y-4">
                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {renderLabel(
                      'Include in Annual Review Report',
                      'Include in the year-end review?'
                    )}
                  </label>
                  <Select
                    value={newEvent.add_to_annual_plan_review}
                    onChange={(value) =>
                      setNewEvent({ ...newEvent, add_to_annual_plan_review: value })
                    }
                    style={{ width: '100%' }}
                  >
                    <Option value="Yes">Yes</Option>
                    <Option value="No">No</Option>
                  </Select>
                </div>
                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {renderLabel(
                      "Include in Next Year's Plan Data",
                      'Carry forward to next year?'
                    )}
                  </label>
                  <Select
                    value={newEvent.include_in_next_years_master_spreadsheet}
                    onChange={(value) =>
                      setNewEvent({
                        ...newEvent,
                        include_in_next_years_master_spreadsheet: value,
                      })
                    }
                    style={{ width: '100%' }}
                  >
                    <Option value="Yes">Yes</Option>
                    <Option value="No">No</Option>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {renderLabel('Rewrite with AI', 'Have AI improve clarity of description?')}
                </label>
                <Select
                  value={newEvent.rewrite_with_ai}
                  onChange={(value) => setNewEvent({ ...newEvent, rewrite_with_ai: value })}
                  style={{ width: '100%' }}
                >
                  <Option value={true}>Yes</Option>
                  <Option value={false}>No</Option>
                </Select>
              </div>
            </div>
            {/* Action Buttons */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowEventDialog(false)}
                className="px-4 py-2 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={addEvent}
                className="px-4 py-2 bg-gradient-to-bl from-sky-400 to-blue-800 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-colors shadow-md"
              >
                Add New Requirement
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default RequirementsCalendar;